import React from "react";
import AccordionTwoColumnGrid from "@/components/molecules/AccordionTwoColumnGrid";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Control, Controller, UseFormWatch } from "react-hook-form";
import { RegisterCompanyFormInput } from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";
import Group from "@/components/atoms/Group";
import Checkbox from "@/components/atoms/Checkbox";
import CTA from "@/components/atoms/CTA";

interface TermsAndConditionsProps {
  control: Control<RegisterCompanyFormInput>;
  isValid: boolean;
  watch: UseFormWatch<RegisterCompanyFormInput>;
  erroneousSections: string[];
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ control, isValid, watch, erroneousSections }) => {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language;

  function getTermsPath() {
    if (currentLocale.startsWith("en")) {
      return "/terms/Terms_en.pdf";
    } else {
      return "/terms/Terms_nb.pdf";
    }
  }

  const leftContent = (
    <Stack p={25}>
      <SVGAsset name="signature" />
    </Stack>
  );

  const rightContent = (
    <Stack h="100%" align="center" justify="center" gap={15}>
      <Text size="lg" display="technical" tt="uppercase" fw={500} ff="heading" mt={20}>
        {t("registerCompany:termsAndConditions.title")}
      </Text>
      <Group gap={10} align="center">
        <Text size="sm" color="secondary">
          {t("registerCompany:termsAndConditions.reason")}
        </Text>
      </Group>
      <a href={getTermsPath()} target="_blank" rel="noreferrer">
        <Text size="sm" color="tertiary">
          {t("registerCompany:termsAndConditions.readTerms")}
        </Text>
      </a>
      <Controller
        name="partialCompany.terms"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => {
          return (
            <Checkbox
              label={t("registerCompany:termsAndConditions.acceptTerms")}
              data-testid="terms-and-conditions-checkbox"
              onChange={onChange}
              value={value.toString()}
              checked={value}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          );
        }}
      />
      {erroneousSections.length > 0 && (
        <Text size="sm" color="error" ta="center">
          {t("registerCompany:validation.incompleteInfo")}: {erroneousSections.join(", ")}
        </Text>
      )}
      <CTA
        type="submit"
        data-testid={"btn-submit"}
        variant="primary"
        disabled={!isValid || watch("submitted") === true}
      >
        {t("registerCompany:submitButton")}
      </CTA>
    </Stack>
  );

  return <AccordionTwoColumnGrid leftContent={leftContent} rightContent={rightContent} />;
};

export default TermsAndConditions;
